import React from "react";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
} from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import "./App.css";

import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Navigation from "./components/Navigation";

import HomePage from "./components/HomePage";
import ClientPortal from "./components/ClientPortal";
import PendingApproval from "./components/PendingApproval";

import DashboardLayout from "./components/dashboard/DashboardLayout";
import AdminDashboard from "./components/dashboard/AdminDashboard";
import ClientDashboard from "./components/dashboard/ClientDashboard";
import ClientList from "./components/dashboard/ClientList";
import ClientDetail from "./components/dashboard/ClientDetail";
import BillingPage from "./components/dashboard/BillingPage";
import QuoteBuilder from "./components/dashboard/QuoteBuilder";
import QuoteView from "./components/dashboard/QuoteView";
import QuotesList from "./components/dashboard/QuotesList";
import InvoiceBuilder from "./components/dashboard/InvoiceBuilder";
import InvoicesList from "./components/dashboard/InvoicesList";
import ServiceCatalog from "./components/dashboard/ServiceCatalog";
import Support from "./components/dashboard/Support";
import AccountPage from "./components/dashboard/AccountPage";
import MessagesPage from "./components/dashboard/MessagesPage";
import AdminMessages from "./components/dashboard/AdminMessages";
import ProjectsList from "./components/dashboard/ProjectsList";
import ProjectDetail from "./components/dashboard/ProjectDetail";

function App() {
    return (
        <I18nextProvider i18n={i18n}>
            <ThemeProvider>
            <AuthProvider>
                <Router>
                    <div className="App">
                        <Navigation />
                        <Routes>
                            {/* Public routes */}
                            <Route path="/" element={<HomePage />} />
                            <Route
                                path="/client-portal"
                                element={<ClientPortal />}
                            />
                            <Route
                                path="/pending"
                                element={<PendingApproval />}
                            />

                            {/* Client routes */}
                            <Route
                                path="/dashboard"
                                element={
                                    <ProtectedRoute>
                                        <DashboardLayout>
                                            <ClientDashboard />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/dashboard/quotes"
                                element={
                                    <ProtectedRoute>
                                        <DashboardLayout>
                                            <QuotesList />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/dashboard/quotes/:id"
                                element={
                                    <ProtectedRoute>
                                        <DashboardLayout>
                                            <QuoteView />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/dashboard/invoices"
                                element={
                                    <ProtectedRoute>
                                        <DashboardLayout>
                                            <InvoicesList />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/dashboard/invoices/:id"
                                element={
                                    <ProtectedRoute>
                                        <DashboardLayout>
                                            <InvoiceBuilder readOnly />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/dashboard/support"
                                element={
                                    <ProtectedRoute>
                                        <DashboardLayout>
                                            <Support />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/dashboard/messages"
                                element={
                                    <ProtectedRoute>
                                        <DashboardLayout>
                                            <MessagesPage />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/dashboard/account"
                                element={
                                    <ProtectedRoute>
                                        <DashboardLayout>
                                            <AccountPage />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />

                            {/* Admin routes */}
                            <Route
                                path="/admin"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <DashboardLayout>
                                            <AdminDashboard />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />
                            {/* New client management routes */}
                            <Route
                                path="/admin/clients"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <DashboardLayout>
                                            <ClientList />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/clients/:id"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <DashboardLayout>
                                            <ClientDetail />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />

                            <Route
                                path="/admin/messages"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <DashboardLayout>
                                            <AdminMessages />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/account"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <DashboardLayout>
                                            <AccountPage />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />

                            {/* Billing (unified Quotes + Invoices) */}
                            <Route
                                path="/admin/billing"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <DashboardLayout>
                                            <BillingPage />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />

                            {/* Legacy list redirects → BillingPage */}
                            <Route
                                path="/admin/quotes"
                                element={
                                    <Navigate
                                        to="/admin/billing?tab=quotes"
                                        replace
                                    />
                                }
                            />
                            <Route
                                path="/admin/invoices"
                                element={
                                    <Navigate
                                        to="/admin/billing?tab=invoices"
                                        replace
                                    />
                                }
                            />

                            {/* Quote builder (create / edit) */}
                            <Route
                                path="/admin/quotes/new"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <DashboardLayout>
                                            <QuoteBuilder />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/quotes/:id"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <DashboardLayout>
                                            <QuoteBuilder />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />

                            {/* Invoice builder (create / edit) */}
                            <Route
                                path="/admin/invoices/new"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <DashboardLayout>
                                            <InvoiceBuilder />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/invoices/:id"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <DashboardLayout>
                                            <InvoiceBuilder />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/services"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <DashboardLayout>
                                            <ServiceCatalog />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/projects"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <DashboardLayout>
                                            <ProjectsList />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/projects/:id"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <DashboardLayout>
                                            <ProjectDetail />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />

                            {/* Client project views */}
                            <Route
                                path="/dashboard/projects"
                                element={
                                    <ProtectedRoute>
                                        <DashboardLayout>
                                            <ProjectsList />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/dashboard/projects/:id"
                                element={
                                    <ProtectedRoute>
                                        <DashboardLayout>
                                            <ProjectDetail readOnly />
                                        </DashboardLayout>
                                    </ProtectedRoute>
                                }
                            />

                            <Route
                                path="*"
                                element={<Navigate to="/" replace />}
                            />
                        </Routes>
                    </div>
                </Router>
            </AuthProvider>
            </ThemeProvider>
        </I18nextProvider>
    );
}

export default App;
